import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { formatMoney } from '../utils/currency';
import { overheadAllocation, businesses as businessesApi } from '../api';
import { Calculator, Trash2, RefreshCw, Send, RotateCcw, Info } from 'lucide-react';

const METHOD_OPTIONS = [
  { value: 'equal_split', label: 'Equal Split (auto-divide equally)' },
  { value: 'fixed_percent', label: 'Fixed / Custom Percentage' },
  { value: 'custom_percent', label: 'Custom Percentage' },
  { value: 'manual_amount', label: 'Manual Amounts' },
  { value: 'revenue_percent', label: 'Revenue Percentage (auto)' },
  { value: 'gross_profit_percent', label: 'Gross Profit Percentage (auto)' },
];

const OverheadAllocation = ({ businessId, businessName, businessCurrency = 'USD' }) => {
  const { toast } = useToast();
  const [divisions, setDivisions] = useState([]);  // subsidiaries only (do NOT allocate to parent)
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  
  // Form state
  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [method, setMethod] = useState('equal_split');
  const [corporateAmount, setCorporateAmount] = useState('');
  const [cadence, setCadence] = useState('period');  // 'period' | 'range'
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [allocationDate, setAllocationDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [manualLines, setManualLines] = useState({});  // {division_id: {amount, percentage}}
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  
  useEffect(() => {
    if (!businessId) return;
    (async () => {
      try {
        const res = await businessesApi.getAll();
        const all = res.data || [];
        const parent = all.find((b) => b.id === businessId);
        if (parent && parent.is_holding_company) {
          const subs = all.filter((b) => (parent.subsidiary_ids || []).includes(b.id));
          setDivisions(subs);
          setSelectedDivisions(subs.map((s) => s.id));
        }
      } catch (e) {
        // ignore
      }
      fetchList();
    })();
  }, [businessId]);
  
  const fetchList = async () => {
    if (!businessId) return;
    try {
      setLoadingList(true);
      const res = await overheadAllocation.list(businessId);
      setList(res.data || []);
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed to load allocations', variant: 'destructive' });
    } finally {
      setLoadingList(false);
    }
  };
  
  const resetForm = () => {
    setEditingId(null);
    setMethod('equal_split');
    setCorporateAmount('');
    setCadence('period');
    setPeriod(new Date().toISOString().slice(0, 7));
    setStartDate('');
    setEndDate('');
    setAllocationDate(new Date().toISOString().slice(0, 10));
    setNotes('');
    setSelectedDivisions(divisions.map((s) => s.id));
    setManualLines({});
    setPreview(null);
  };
  
  const buildPayload = () => {
    const payload = {
      business_id: businessId,
      method,
      corporate_amount: parseFloat(corporateAmount) || 0,
      allocation_date: allocationDate,
      notes,
      division_ids: selectedDivisions,
    };
    if (cadence === 'period') {
      payload.period = period;
    } else {
      payload.start_date = startDate || null;
      payload.end_date = endDate || null;
    }
    // Manual lines needed for manual_amount, custom_percent, fixed_percent
    if (['manual_amount', 'custom_percent', 'fixed_percent'].includes(method)) {
      payload.lines = selectedDivisions.map((did) => {
        const row = manualLines[did] || {};
        if (method === 'manual_amount') {
          return { division_id: did, amount: parseFloat(row.amount) || 0 };
        }
        return { division_id: did, percentage: parseFloat(row.percentage) || 0 };
      });
    }
    return payload;
  };
  
  const handlePreview = async () => {
    try {
      setPreviewing(true);
      const res = await overheadAllocation.preview(buildPayload());
      setPreview(res.data);
    } catch (e) {
      toast({ title: 'Preview failed', description: e?.response?.data?.detail || 'Could not compute preview', variant: 'destructive' });
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };
  
  const handleSave = async (status) => {
    const payload = { ...buildPayload(), status };
    try {
      if (editingId) {
        await overheadAllocation.update(editingId, payload);
      } else {
        await overheadAllocation.create(payload);
      }
      toast({ title: 'Saved', description: `Allocation ${status === 'posted' ? 'posted' : 'saved as draft'}.` });
      setOpenForm(false);
      resetForm();
      fetchList();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Could not save', variant: 'destructive' });
    }
  };
  
  const handlePost = async (id) => {
    try {
      await overheadAllocation.post(id);
      toast({ title: 'Posted', description: 'Allocation is now posted.' });
      fetchList();
    } catch (e) {
      toast({ title: 'Post failed', description: e?.response?.data?.detail || 'Could not post', variant: 'destructive' });
    }
  };
  
  const handleReverse = async (id) => {
    if (!window.confirm('Reverse this posted allocation? A reversal entry will be created for the audit trail.')) return;
    try {
      await overheadAllocation.reverse(id);
      toast({ title: 'Reversed', description: 'Allocation reversed.' });
      fetchList();
    } catch (e) {
      toast({ title: 'Reverse failed', description: e?.response?.data?.detail || 'Could not reverse', variant: 'destructive' });
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this draft allocation?')) return;
    try {
      await overheadAllocation.remove(id);
      toast({ title: 'Deleted' });
      fetchList();
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || 'Could not delete', variant: 'destructive' });
    }
  };
  
  const openNew = () => {
    resetForm();
    setOpenForm(true);
  };
  
  const openEdit = (a) => {
    setEditingId(a.id);
    setMethod(a.method || 'equal_split');
    setCorporateAmount(String(a.corporate_amount || ''));
    setCadence(a.period ? 'period' : 'range');
    setPeriod(a.period || new Date().toISOString().slice(0, 7));
    setStartDate(a.start_date || '');
    setEndDate(a.end_date || '');
    setAllocationDate(a.allocation_date || new Date().toISOString().slice(0, 10));
    setNotes(a.notes || '');
    setSelectedDivisions((a.lines || []).map((l) => l.division_id));
    const lm = {};
    (a.lines || []).forEach((l) => {
      lm[l.division_id] = { amount: l.amount, percentage: l.percentage };
    });
    setManualLines(lm);
    setPreview({ lines: a.lines, total_amount: (a.lines || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0), currency: a.currency });
    setOpenForm(true);
  };
  
  const showManualInputs = ['manual_amount', 'custom_percent', 'fixed_percent'].includes(method);
  const needsDateRangeForBasis = ['revenue_percent', 'gross_profit_percent'].includes(method);
  const totalManualAmount = useMemo(() => {
    if (method !== 'manual_amount') return 0;
    return Object.values(manualLines).reduce((s, r) => s + (parseFloat(r?.amount) || 0), 0);
  }, [manualLines, method]);
  const totalManualPercent = useMemo(() => {
    if (!['custom_percent', 'fixed_percent'].includes(method)) return 0;
    return Object.values(manualLines).reduce((s, r) => s + (parseFloat(r?.percentage) || 0), 0);
  }, [manualLines, method]);
  
  return (
    <div className="space-y-6">
      <Card className="shadow-md">
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
            <div>
              <CardTitle className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Calculator className="h-5 w-5 text-blue-600" />
                Corporate Overhead Allocation
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Internal management-accounting entries only. These allocations distribute Corporate / Head Office costs across operating divisions for management reporting. They never touch the legal company's General Ledger, so consolidated P&amp;L is never double-counted.
              </p>
            </div>
            <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="new-allocation-btn">
              <Calculator className="h-4 w-4" /> New Allocation
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {divisions.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed rounded-lg text-gray-500 text-sm">
              This business has no divisions configured. Mark it as a Legal Entity and add operating divisions to enable overhead allocation.
            </div>
          ) : loadingList ? (
            <div className="py-8 text-center text-gray-500">Loading…</div>
          ) : list.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed rounded-lg text-gray-500 text-sm">
              No allocations yet. Click <b>New Allocation</b> to create one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="py-2 px-3 text-left">Date</th>
                    <th className="py-2 px-3 text-left">Period</th>
                    <th className="py-2 px-3 text-left">Method</th>
                    <th className="py-2 px-3 text-right">Amount</th>
                    <th className="py-2 px-3 text-left">Status</th>
                    <th className="py-2 px-3 text-left">Notes</th>
                    <th className="py-2 px-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {list.map((a) => (
                    <tr key={a.id} className="hover:bg-gray-50" data-testid={`allocation-row-${a.id}`}>
                      <td className="py-2 px-3">{a.allocation_date}</td>
                      <td className="py-2 px-3">{a.period || `${a.start_date || ''}${a.start_date ? ' → ' : ''}${a.end_date || ''}` || '—'}</td>
                      <td className="py-2 px-3 text-xs">{METHOD_OPTIONS.find(m => m.value === a.method)?.label || a.method}</td>
                      <td className="py-2 px-3 text-right font-mono">{formatMoney(a.corporate_amount, a.currency || businessCurrency)}</td>
                      <td className="py-2 px-3">
                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${a.status === 'posted' ? 'bg-green-100 text-green-800' : a.status === 'draft' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-200 text-gray-700'}`}>
                          {a.status}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-xs text-gray-600 max-w-[240px] truncate">{a.notes || '—'}</td>
                      <td className="py-2 px-3 text-center">
                        <div className="flex justify-center gap-1">
                          {a.status === 'draft' && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => openEdit(a)} className="h-7 px-2 text-xs" data-testid={`edit-alloc-${a.id}`}>Edit</Button>
                              <Button variant="ghost" size="sm" onClick={() => handlePost(a.id)} className="h-7 px-2 text-xs text-green-700" data-testid={`post-alloc-${a.id}`}><Send className="h-3 w-3 mr-1" />Post</Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)} className="h-7 px-2 text-xs text-red-600" data-testid={`delete-alloc-${a.id}`}><Trash2 className="h-3 w-3" /></Button>
                            </>
                          )}
                          {a.status === 'posted' && (
                            <Button variant="ghost" size="sm" onClick={() => handleReverse(a.id)} className="h-7 px-2 text-xs text-orange-700" data-testid={`reverse-alloc-${a.id}`}><RotateCcw className="h-3 w-3 mr-1" />Reverse</Button>
                          )}
                          {a.status === 'reversed' && (
                            <span className="text-xs text-gray-500 italic">reversed</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      
      <Dialog open={openForm} onOpenChange={(v) => { setOpenForm(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Allocation' : 'New Corporate Overhead Allocation'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Allocation Method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="alloc-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METHOD_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Corporate Amount ({businessCurrency})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={corporateAmount}
                  onChange={(e) => setCorporateAmount(e.target.value)}
                  placeholder="e.g. 200000"
                  data-testid="alloc-amount-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Cadence</Label>
                <Select value={cadence} onValueChange={setCadence}>
                  <SelectTrigger data-testid="alloc-cadence-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="period">Monthly Close (period)</SelectItem>
                    <SelectItem value="range">Ad-hoc (date range)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {cadence === 'period' ? (
                <div>
                  <Label>Period (YYYY-MM)</Label>
                  <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="alloc-period-input" />
                </div>
              ) : (
                <>
                  <div>
                    <Label>Start Date</Label>
                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                  </div>
                </>
              )}
              <div>
                <Label>Allocation Date</Label>
                <Input type="date" value={allocationDate} onChange={(e) => setAllocationDate(e.target.value)} data-testid="alloc-date-input" />
              </div>
            </div>
            
            <div>
              <Label>Divisions</Label>
              <div className="border rounded-lg p-3 bg-gray-50 space-y-2 max-h-56 overflow-y-auto">
                {divisions.map((d) => {
                  const checked = selectedDivisions.includes(d.id);
                  const row = manualLines[d.id] || {};
                  return (
                    <div key={d.id} className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setSelectedDivisions((prev) => e.target.checked ? [...prev, d.id] : prev.filter((x) => x !== d.id));
                        }}
                        className="h-4 w-4 accent-blue-600"
                        data-testid={`alloc-div-${d.id}`}
                      />
                      <span className="flex-1 text-sm">{d.name} <span className="text-xs text-gray-500">({d.currency || 'USD'})</span></span>
                      {showManualInputs && checked && (
                        <>
                          {method === 'manual_amount' ? (
                            <Input
                              type="number" step="0.01" className="w-32 h-8 text-sm"
                              placeholder="Amount"
                              value={row.amount ?? ''}
                              onChange={(e) => setManualLines((prev) => ({ ...prev, [d.id]: { ...row, amount: e.target.value } }))}
                              data-testid={`alloc-amount-${d.id}`}
                            />
                          ) : (
                            <Input
                              type="number" step="0.01" className="w-24 h-8 text-sm"
                              placeholder="%"
                              value={row.percentage ?? ''}
                              onChange={(e) => setManualLines((prev) => ({ ...prev, [d.id]: { ...row, percentage: e.target.value } }))}
                              data-testid={`alloc-percent-${d.id}`}
                            />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              {method === 'manual_amount' && (
                <p className="text-xs mt-1 flex items-center gap-2">
                  <Info className="h-3 w-3" /> Total: {formatMoney(totalManualAmount, businessCurrency)} / {formatMoney(parseFloat(corporateAmount) || 0, businessCurrency)}
                  {Math.abs(totalManualAmount - (parseFloat(corporateAmount) || 0)) > 0.01 && (
                    <span className="text-red-600 font-semibold">— Difference: {formatMoney(totalManualAmount - (parseFloat(corporateAmount) || 0), businessCurrency)}</span>
                  )}
                </p>
              )}
              {['custom_percent', 'fixed_percent'].includes(method) && (
                <p className="text-xs mt-1 flex items-center gap-2">
                  <Info className="h-3 w-3" /> Total percentage: {totalManualPercent.toFixed(2)}%
                  {Math.abs(totalManualPercent - 100) > 0.01 && (
                    <span className="text-red-600 font-semibold">— Must sum to 100%</span>
                  )}
                </p>
              )}
              {needsDateRangeForBasis && (
                <p className="text-xs mt-1 text-gray-500">Allocation basis is calculated automatically from each division's {method === 'revenue_percent' ? 'revenue' : 'gross profit'} within the chosen period / date range.</p>
              )}
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="e.g. Corporate rent + insurance for January 2026" data-testid="alloc-notes-input" />
            </div>
            
            <div className="flex gap-2">
              <Button onClick={handlePreview} variant="outline" className="gap-2" disabled={previewing} data-testid="alloc-preview-btn">
                <RefreshCw className={`h-4 w-4 ${previewing ? 'animate-spin' : ''}`} /> Preview
              </Button>
              <Button onClick={() => handleSave('draft')} variant="outline" data-testid="alloc-save-draft-btn">Save Draft</Button>
              <Button onClick={() => handleSave('posted')} className="bg-green-600 hover:bg-green-700" data-testid="alloc-post-btn">
                <Send className="h-4 w-4 mr-2" /> Post Allocation
              </Button>
            </div>
            
            {preview && preview.lines && preview.lines.length > 0 && (
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-blue-900">Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left">
                        <th className="py-1 px-2">Division</th>
                        <th className="py-1 px-2 text-right">%</th>
                        <th className="py-1 px-2 text-right">Amount ({preview.currency})</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.lines.map((l) => (
                        <tr key={l.division_id} className="border-t border-blue-200">
                          <td className="py-1 px-2">{l.division_name}</td>
                          <td className="py-1 px-2 text-right">{(l.percentage || 0).toFixed(2)}%</td>
                          <td className="py-1 px-2 text-right font-mono">{formatMoney(l.amount, preview.currency)}</td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-blue-400 font-semibold">
                        <td className="py-1 px-2">Total Allocated</td>
                        <td className="py-1 px-2 text-right">{(preview.total_percentage || 0).toFixed(2)}%</td>
                        <td className="py-1 px-2 text-right font-mono">{formatMoney(preview.total_amount, preview.currency)}</td>
                      </tr>
                      <tr>
                        <td className="py-1 px-2">Remaining</td>
                        <td></td>
                        <td className={`py-1 px-2 text-right font-mono ${Math.abs(preview.remaining || 0) > 0.005 ? 'text-red-600 font-bold' : 'text-gray-400'}`}>
                          {formatMoney(preview.remaining, preview.currency)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OverheadAllocation;
